import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@emotion/react'
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import { faSortAmountDown, faSortAmountUp } from '@fortawesome/free-solid-svg-icons'
import { CustomerListItem, SearchInputForm } from '@kc/alphabrands-component-library'

import useAPIClient from '../../hooks/useAPIClient'
import { Customer } from '../../api/types'
import {
  CustomerList,
  SortButton,
  ListInfoRow,
  FilterRow,
  HeaderContainer,
  SearchContainer,
} from './AssetsPage.styles'
import HeaderText from '../../styles/HeaderText'
import Loader from '../../components/Loader'
import DataNotFound from '../../components/DataNotFound'
import PageContent from '../../styles/PageContent'
import { useFilters, useFiltersUrlSync, prepareFilters, getStates } from './helpers'
import FilterInput from '../../components/FilterInput'
import FiltersArea from '../../components/FiltersArea'
import StandardError from '../../components/StandardError'
import { FilterProps } from './types'
import countryCodes from './CountryCodes'
import useModuleCase from '../../hooks/useModuleName'

export type Filter = {
  filter: string
  type: string
}
const limit = 50
const CustomersList: React.FC = () => {
  const apiClient = useAPIClient()
  const { t } = useTranslation()
  const theme = useTheme()
  const [customers, setCustomers] = useState<Customer[]>([])
  const [loading, setLoading] = useState(false)
  const [refreshExistingList, setRefreshExistingList] = useState(false)
  const [error, setError] = useState('')
  const [offset, setOffset] = useState(1)
  const [sortDirection, setSortDirection] = useState('asc')
  const [searchTerm, setSearchTerm] = useState('')
  const { filters, handleAddFilter, handleDeleteFilter, setFilters, clearFilters } = useFilters()
  const effectCalls = useRef(0)
  const isFirstRender = effectCalls.current === 0
  const { initFiltersFromUrl, syncFiltersToUrl } = useFiltersUrlSync(filters, setFilters)
  const upperCase = useModuleCase()

  const fetchCustomers = useCallback(
    async (clearExistingList: boolean = false) => {
      try {
        setLoading(true)
        setRefreshExistingList(clearExistingList)
        const filtersAsParams = prepareFilters(filters)

        if (searchTerm !== '') filtersAsParams['search'] = searchTerm
        const { data } = await apiClient.listCustomers(
          limit,
          clearExistingList ? 0 : offset,
          sortDirection,
          filtersAsParams,
        )
        clearExistingList ? setCustomers(data) : setCustomers([...customers, ...data])
      } catch (err) {
        setError(t('something_went_wrong'))
      } finally {
        setLoading(false)
        setRefreshExistingList(false)
      }
    },
    [filters, searchTerm, apiClient, offset, sortDirection, customers, t],
  )

  let bottomBoundaryRef: any = useRef(null)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const scrollObserver = (node: HTMLElement) => {
    new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.intersectionRatio > 0) {
          setOffset(offset + limit)
        }
      })
    }).observe(node)
  }

  useEffect(() => {
    if (!isFirstRender && customers.length < offset) {
      fetchCustomers()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset])

  useEffect(() => {
    if (isFirstRender) {
      initFiltersFromUrl()
    } else {
      syncFiltersToUrl()
      setOffset(1)
      fetchCustomers(true)
    }
    effectCalls.current++

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortDirection, filters])

  useEffect(() => {
    const { current } = bottomBoundaryRef
    if (current) {
      scrollObserver(current)
    }
  })
  /**
   * return true only when we have more data to show based on the selected query parameters
   */
  const showBottomBoundary = () => !loading && customers.length >= limit + offset - 1

  const renderCustomerList = () => (
    <>
      <ListInfoRow>
        <h2>{`${t('partners')} (${customers.length})`}</h2>
        <SortButton
          aria-label={t('sort')}
          disabled={loading}
          onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
        >
          <Icon icon={sortDirection === 'asc' ? faSortAmountDown : faSortAmountUp} size="2x" />
        </SortButton>
      </ListInfoRow>
      <CustomerList data-testid="customer-list">
        {customers.map(
          ({ accountid, accountname: customerName, countrycode, gcmid, city }, index) => (
            <Link key={'key_' + index} to={`/my-partners-and-equipment/${gcmid}`}>
              <CustomerListItem
                key={accountid}
                name={customerName}
                location={countrycode && city ? `${city}, ${countryCodes[countrycode]}` : ''}
                titleColor={theme.listItem.titleColor}
                descriptionColor={theme.listItem.descriptionColor}
                titleHoverColor={theme.linkHoverColor}
              />
            </Link>
          ),
        )}
      </CustomerList>
      {loading && offset > 50 && <Loader loadingText={t('loading')} />}
      {showBottomBoundary() && (
        <div
          id="page-bottom-boundary"
          style={{ visibility: 'hidden' }}
          ref={bottomBoundaryRef}
        ></div>
      )}
    </>
  )

  return (
    <PageContent>
      <HeaderContainer>
        <HeaderText>{upperCase(t('my_partners_and_equipment'))}</HeaderText>
        <SearchContainer>
          <SearchInputForm
            className={theme.brand === 'demag' ? 'rounded' : ''}
            onChange={(v) => setSearchTerm(v)}
            id="partners-search-input"
            label={t('search_partners_by_name')}
            buttonColor={theme.button.primary.color}
            buttonHoverColor={theme.button.primary.hover}
            submitButton
            onSubmit={() => fetchCustomers(true)}
          />
        </SearchContainer>
      </HeaderContainer>
      <Filters
        filters={filters}
        onFilterAdd={handleAddFilter}
        onFilterDeleteClick={handleDeleteFilter}
        clearFilters={clearFilters}
      />
      {refreshExistingList && <Loader loadingText={t('loading')} />}
      {!refreshExistingList && customers.length > 0 && renderCustomerList()}
      {!loading && !error && customers.length === 0 && (
        <DataNotFound notFoundText={t('no_partners_error')} />
      )}
      {error && <StandardError />}
    </PageContent>
  )
}

const Filters: React.FC<FilterProps> = ({
  onFilterAdd,
  filters,
  onFilterDeleteClick,
  clearFilters,
}) => {
  const { t } = useTranslation()
  const country = filters.find((filter) => filter.type === 'country')?.filter.toLowerCase()
  const states = getStates(country)
  const theme = useTheme()

  return (
    <FiltersArea
      filters={filters}
      onFilterDeleteClick={onFilterDeleteClick}
      clearFilters={clearFilters}
      labelBackgroundColor={theme.button.primary.color}
      labelTextColor={theme.button.primary.textColor}
      linkColor={theme.linkColor}
      linkHoverColor={theme.linkHoverColor}
    >
      <FilterRow>
        <FilterInput
          type="country"
          title={t('country')}
          items={Object.values(countryCodes)}
          onItemSelect={onFilterAdd}
        />

        <FilterInput
          type="state"
          disabled={states.length === 0}
          title={t('state')}
          items={states}
          onItemSelect={onFilterAdd}
        />

        <FilterInput
          type="AccountClassification"
          title={t('classification')}
          items={['Maintained']}
          onItemSelect={onFilterAdd}
        />
      </FilterRow>
    </FiltersArea>
  )
}

export default CustomersList
